// src/oura/lifecycle.ts
//
// Where a business is, answered in one place (ourteam-frontend#3713).
//
// Every surface used to work this out inline from some mix of `state`, `plan`
// and `latest_assessment`, which is how the same business could read as a
// prospect on one screen and a client on another. This is that answer, typed,
// declared once.

import type { AeoBusiness } from "@service/aeo";
import type { Engagement } from "@service/aeo/clientReview";

/**
 * The stages a business moves through, in order.
 *
 * Deliberately NOT the raw `state` enum. `state` is three values the backend
 * stores (`prospect | client | churned`) and it cannot tell a signed client
 * whose work has not begun from one mid-delivery — which is the distinction an
 * operator scanning the list actually needs.
 */
export type Lifecycle = "prospect" | "quoted" | "active" | "churned";

export const LIFECYCLE_LABEL: Record<Lifecycle, string> = {
    prospect: "Prospect",
    quoted: "Quoted",
    active: "Active",
    churned: "Churned",
};

/**
 * Ordered for sorting and for reading: a list grouped by stage should run from
 * "nobody has sold this yet" to "this one has left".
 */
export const LIFECYCLE_ORDER: readonly Lifecycle[] = ["prospect", "quoted", "active", "churned"];

/**
 * One palette, so a stage is the same colour on every screen. Here rather than
 * in a screen because the second screen to render a stage is how two palettes
 * start — which is the same failure as two lifecycle derivations, in CSS.
 */
export const LIFECYCLE_CHIP: Record<Lifecycle, string> = {
    prospect: "border-gray-200 bg-gray-50 text-gray-600",
    quoted: "border-blue-200 bg-blue-50 text-blue-700",
    active: "border-emerald-200 bg-emerald-50 text-emerald-700",
    churned: "border-gray-200 bg-gray-100 text-gray-500",
};

/**
 * Where this business is.
 *
 * `engagement` is optional and the answer degrades honestly without it: a
 * business with no engagement in hand can still be told apart as churned or
 * prospect from `state` alone, and anything better than that needs the record.
 * The backend's own `lifecycle` block (ourteam-backend#4807) is not out yet;
 * when it is, it belongs here and nowhere else, and callers do not change.
 *
 * Churned wins over everything, including a running engagement. A business that
 * has left is not "active with work outstanding" — that framing is how a
 * departed client keeps appearing in a delivery queue.
 */
export function lifecycleOf(
    business: Pick<AeoBusiness, "state">,
    engagement?: Engagement | null,
): Lifecycle {
    if (business.state === "churned") return "churned";
    if (!engagement) {
        // No record to read. `state: "client"` without one is a business
        // somebody marked as won before the plan was written — quoted is the
        // honest reading, and it is the one that shows up as needing work.
        return business.state === "client" ? "quoted" : "prospect";
    }
    // `startedAt` is null until payment clears, which is exactly the line
    // between "sold" and "being delivered".
    return engagement.startedAt ? "active" : "quoted";
}

/* ------------------------------------------------------------------------- *
 * The Businesses list's tabs
 * ------------------------------------------------------------------------- */

/**
 * Where a row sits on the Businesses list (YW, 15 Oct 2026: "filter the table
 * of clients by active plan"). Four tabs that never overlap, so every row is in
 * exactly one and the counts add up to All.
 *
 * Not `Lifecycle`. That is the commercial answer for one business with its
 * engagement in hand; this is the list's, and it adds the one thing only the
 * list reads: whether the client's set-up is finished.
 */
export type ListStage = "active" | "onboarding" | "prospect" | "ended";

/** The tab order: the clients we work for first, the long tail of prospects after. */
export const LIST_STAGE_ORDER: readonly ListStage[] = ["active", "onboarding", "prospect", "ended"];

export const LIST_STAGE_LABEL: Record<ListStage, string> = {
    active: "Active",
    onboarding: "Onboarding",
    prospect: "Prospect",
    ended: "Ended",
};

/** Each stage's dot, in its `LIFECYCLE_CHIP` hue. Ended is a hollow ring: the plan is over, not paused. */
export const LIST_STAGE_DOT: Record<ListStage, string> = {
    active: "bg-emerald-500",
    onboarding: "bg-amber-500",
    prospect: "bg-gray-400",
    ended: "border-[1.5px] border-gray-400",
};

type ListRow = Pick<AeoBusiness, "state" | "engagement_status" | "lifecycle" | "plan_started_on" | "plan_ends_on">;

/**
 * Where this row sits, when the server does not say. A list row carries its
 * own `stage` (ourteam-backend#6091) and that wins; this is the fallback for an
 * older server. Checked in this order:
 *
 * 1. Ended: marked churned, the server says ended, the engagement ended or was
 *    cancelled, or the term's last day has passed.
 * 2. Prospect, when the server says so: `lifecycle.state` is "prospect", or the
 *    engagement it sends is neither signed nor active (a draft quote). These
 *    beat the older hints below, so a stale `state: "client"` or an onboarding
 *    record cannot pull such a row into Onboarding (Jack, #4220).
 * 3. Prospect otherwise: nothing signed. No signed engagement, not marked a
 *    client, no onboarding record.
 * 4. Onboarding: signed, and the set-up is open or the plan has not started.
 * 5. Active: signed, set up, and the term is running.
 *
 * `setUp` is the onboarding record's answer: null when the client has none
 * (signed before onboarding existed, or set up by hand), which counts as done.
 * `today` is DATE-ONLY, passed in so the rule never reads a clock.
 *
 * Degrades without the plan fields. A list that sends no start date has a
 * signed client running once its set-up is done, which is what the list showed
 * before the fields existed; a client marked won with no plan on file waits in
 * Onboarding, because a plan has to be written before any work can run.
 */
export function listStageOf(row: ListRow, setUp: "done" | "open" | null, today: string): ListStage {
    const server = row.lifecycle?.state ?? null;
    const status = row.engagement_status ?? null;
    if (row.state === "churned" || server === "ended" || status === "ended" || status === "cancelled") return "ended";
    if (row.plan_ends_on && row.plan_ends_on < today) return "ended";

    const engaged = status === "signed" || status === "active";
    const serverEngaged = server === "signed" || server === "active";

    // The server's own answer wins over the older hints. Starting onboarding
    // seeds a signed engagement, so a client being set up never reads
    // "prospect" here.
    if (server === "prospect") return "prospect";
    if (status !== null && !engaged && !serverEngaged) return "prospect";

    const signed = engaged || serverEngaged || row.state === "client" || setUp !== null;
    if (!signed) return "prospect";
    if (setUp === "open") return "onboarding";

    // Is the term running? The server's word first, then the start date, then,
    // on a list that sends neither, a signed engagement or a finished set-up.
    const running =
        serverEngaged ? server === "active"
            : row.plan_started_on !== undefined ? !!row.plan_started_on && row.plan_started_on <= today
                : engaged || setUp === "done";
    return running ? "active" : "onboarding";
}
